import React from 'react';
import { DollarSign, Sparkles, CheckCircle } from 'lucide-react';

const Pricing = () => {
  const features = [
    "Professional vertical wall printer",
    "Fade-resistant, permanent ink",
    "Indoor and outdoor durability",
    "Custom design consultation",
    "Precise color matching",
    "Fast turnaround times"
  ];

  return (
    <section id="pricing" className="py-24 bg-black relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-purple-600 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-purple-800 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-5xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-5xl md:text-6xl font-bold text-white mb-4">
            <span className="text-purple-400">Pricing</span> That Scales
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Investment in art that transforms your space permanently
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-purple-500 to-transparent mx-auto mt-6"></div>
        </div>

        {/* Main Pricing Card */}
        <div className="bg-gradient-to-br from-gray-900 to-black border-2 border-purple-500 rounded-2xl p-10 md:p-12 shadow-2xl shadow-purple-500/20 mb-12">
          <div className="flex items-center justify-center mb-8">
            <div className="w-20 h-20 bg-purple-600/20 rounded-full flex items-center justify-center">
              <DollarSign className="w-10 h-10 text-purple-400" />
            </div>
          </div>

          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold text-white mb-4">Average Mural Pricing</h3>
            <div className="flex items-baseline justify-center gap-2 mb-4">
              <span className="text-5xl md:text-6xl font-bold text-purple-400">$15-$35</span>
              <span className="text-2xl text-gray-400">per sq ft</span>
            </div>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Pricing varies by project complexity, location, artwork style, and surface preparation requirements
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-start gap-3 bg-purple-600/10 rounded-lg p-4 border border-purple-500/20 hover:border-purple-500/50 transition-colors"
              >
                <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                <span className="text-gray-300">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Additional Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-6 text-center hover:border-purple-500 transition-all duration-300">
            <Sparkles className="w-10 h-10 text-purple-400 mx-auto mb-4" />
            <h4 className="text-white font-bold text-lg mb-2">Small Projects</h4>
            <p className="text-gray-400 text-sm">Accent walls and residential features starting at budget-friendly rates</p>
          </div>

          <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-6 text-center hover:border-purple-500 transition-all duration-300">
            <Sparkles className="w-10 h-10 text-purple-400 mx-auto mb-4" />
            <h4 className="text-white font-bold text-lg mb-2">Commercial Scale</h4>
            <p className="text-gray-400 text-sm">Building-scale murals and storefront art with volume discounts</p>
          </div>

          <div className="bg-gray-900/50 border border-gray-800 rounded-lg p-6 text-center hover:border-purple-500 transition-all duration-300">
            <Sparkles className="w-10 h-10 text-purple-400 mx-auto mb-4" />
            <h4 className="text-white font-bold text-lg mb-2">Custom Quotes</h4>
            <p className="text-gray-400 text-sm">Unique projects and artist collaborations priced individually</p>
          </div>
        </div>

        {/* Bottom Note */}
        <div className="text-center mt-12">
          <div className="inline-block bg-purple-600/10 border border-purple-500/30 rounded-lg px-8 py-4">
            <p className="text-purple-300 text-lg">
              Every project is unique. Contact us for a detailed, no-obligation quote
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
